import { createSelector } from "reselect";

import { IntegrationType } from "constants/integrations";

import { selectBusinessRelationsByBusinessIdLookup } from "../businessRelations";
import { getActiveLivestockAgentDeployment } from "../deploymentActiveAgent";
import { selectIntegrationBusinessesByBusinessIdByTypeLookup } from "../integrationBusinesses";
import {
  selectInterestSettingsByDeploymentIdLookup,
  selectResolvedInterestSettingsByBusinessIdLookup,
} from "../interest";
import { getBusinesses, getBusinessLabels } from "../root";
import { selectBusinessTradingTermsByIdLookup } from "../tradingTerms";
import { selectVendorCommissionBandsByBusinessIdLookup } from "../vendorCommissions";
import { selectBranchByIdLookup } from "../branches";

// The shape every business-column-driven ag-grid row is built from - the
// single source of business data for `BusinessColumnDef` and anything that
// composes it. Add new business-derived fields here, not on individual grids.
export interface BusinessAggridDataPacket {
  id: string;
  business: Business & { labels: BusinessLabel[] };
  deployment: Deployment | Record<string, never>;
  tradingTerms: TradingTerm[];
  interestSettings: InterestSettings[] | undefined;
  vendorCommissionBands: VendorCommissionBand[];
  relationships: BusinessRelation[] | undefined;
  xeroIntegrationBusiness: IntegrationBusiness | null;
  branch: Branch | Record<string, never>;
}

export const selectBusinessesAggridDataById = createSelector(
  [
    getBusinesses,
    getBusinessLabels,
    selectResolvedInterestSettingsByBusinessIdLookup,
    selectVendorCommissionBandsByBusinessIdLookup,
    getActiveLivestockAgentDeployment,
    selectBusinessRelationsByBusinessIdLookup,
    selectBusinessTradingTermsByIdLookup,
    selectInterestSettingsByDeploymentIdLookup,
    selectIntegrationBusinessesByBusinessIdByTypeLookup,
    selectBranchByIdLookup,
  ],
  (
    businessByIdLookup: Record<string, Business>,
    businessLabelByIdLookup: Record<string, BusinessLabel>,
    resolvedInterestSettingsByBusinessId: Record<string, InterestSettings[]>,
    vendorCommissionsByBusinessId: Record<string, VendorCommissionBand[]>,
    activeLivestockAgentDeployment: Deployment | Record<string, never>,
    businessRelationsByBusinessIdLookup: Record<string, BusinessRelation[]>,
    tradingTermsByBusinessIdLookup: Record<string, TradingTerm[]>,
    interestSettingsByDeploymentIdLookup: Record<string, InterestSettings[]>,
    integrationBusinessesByTypeByBusinessIdLookup: Record<
      string,
      Record<string, IntegrationBusiness[]>
    >,
    branchByIdLookup: Record<string, Branch>,
  ): Record<string, BusinessAggridDataPacket> => {
    return Object.values(businessByIdLookup).reduce(
      (acc: Record<string, BusinessAggridDataPacket>, business) => {
        const vendorCommissionBands =
          vendorCommissionsByBusinessId[business.id] || [];

        const xeroIntegrationBusiness =
          integrationBusinessesByTypeByBusinessIdLookup[IntegrationType.Xero]?.[
            business.id
          ]?.[0] || null;

        const tradingTerms = tradingTermsByBusinessIdLookup[business.id] || [];
        const businessInterestSettings =
          resolvedInterestSettingsByBusinessId[business.id];
        const deploymentInterestSettings =
          interestSettingsByDeploymentIdLookup[
            activeLivestockAgentDeployment?.id
          ];
        const relationships = businessRelationsByBusinessIdLookup[business.id];
        const branch = branchByIdLookup[business.branchId] || {};
        acc[business.id] = {
          business: {
            ...business,
            labels: (business.labelIds || [])
              .map(id => businessLabelByIdLookup[id])
              .filter(Boolean),
            // Filter hidden things.
            properties: business.properties.filter(
              businessProperty => businessProperty.isShown,
            ),
            buyerWays: business.buyerWays.filter(buyerWay => buyerWay.isShown),
          },
          deployment: activeLivestockAgentDeployment,
          tradingTerms,
          // If we aren't overriding the interest settings, use the deployment interest settings
          interestSettings: business.overrideInterestSettings
            ? businessInterestSettings
            : deploymentInterestSettings,
          vendorCommissionBands,
          relationships,
          xeroIntegrationBusiness,
          branch,
          id: business.id,
        };
        return acc;
      },
      {},
    );
  },
);

export const selectBusinessesAggridData = createSelector(
  [selectBusinessesAggridDataById],
  businessesAggridDataById => Object.values(businessesAggridDataById),
);
